import { PendingTerms } from "../../models/terms/pendingTerms";
import { termsService } from "../terms.service";

export interface TermsAcknowledgementState {
  isOpen: boolean;
  loading: boolean;        // loading pending terms
  acknowledging: boolean;  // posting the acknowledgement
  pendingTerms: PendingTerms | null;
  loadError: boolean;      // GET pending failed
  acknowledgeError: boolean; // POST acknowledge failed
}

type Listener = () => void;

const initialState: TermsAcknowledgementState = {
  isOpen: false,
  loading: false,
  acknowledging: false,
  pendingTerms: null,
  loadError: false,
  acknowledgeError: false,
};

// Framework-agnostic singleton so the HTTP layer (retryWithAuth) and the React
// dialog can share a single "terms acknowledgement required" state.
class TermsAcknowledgementCoordinator {

  private state: TermsAcknowledgementState = initialState;
  private listeners = new Set<Listener>();

  // Shared gate: every request blocked by a terms 403 awaits the same promise,
  // which resolves once the user has successfully acknowledged the terms.
  private gate: Promise<void> | null = null;
  private resolveGate: (() => void) | null = null;

  public getState = (): TermsAcknowledgementState => this.state;

  public subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private setState(patch: Partial<TermsAcknowledgementState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  // Called by the HTTP layer on a terms-acknowledgement 403. Concurrent callers
  // share one gate, one dialog and one pending-terms request.
  public require(): Promise<void> {
    if (!this.gate) {
      this.gate = new Promise<void>((resolve) => {
        this.resolveGate = resolve;
      });
      this.setState({ isOpen: true });
      void this.loadPendingTerms();
    }
    return this.gate;
  }

  public async loadPendingTerms(): Promise<void> {
    this.setState({ loading: true, loadError: false, acknowledgeError: false });
    try {
      const pendingTerms = await termsService.getPendingTerms();
      this.setState({ loading: false, pendingTerms: pendingTerms ?? null });
    }
    catch {
      this.setState({ loading: false, loadError: true });
    }
  }

  public async acknowledge(): Promise<void> {
    if (this.state.acknowledging) {
      return;
    }
    this.setState({ acknowledging: true, acknowledgeError: false });
    try {
      await termsService.acknowledgeTerms();
      this.complete();
    }
    catch {
      this.setState({ acknowledging: false, acknowledgeError: true });
    }
  }

  // Resolve the gate so blocked requests retry, and reset for a possible next cycle.
  private complete(): void {
    const resolve = this.resolveGate;
    this.gate = null;
    this.resolveGate = null;
    this.state = initialState;
    this.listeners.forEach((listener) => listener());
    resolve?.();
  }
}

export const termsAcknowledgement = new TermsAcknowledgementCoordinator();
